import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import apiClient from '@/utils/api';
import { MapPin, Building2, ArrowRight, Search, Home, Key, Palette } from 'lucide-react';

const LOCATION_META = {
  hyderabad: {
    name: 'Hyderabad',
    state: 'Telangana',
    description: 'premium properties in Hyderabad, Telangana',
    areas: ['Gachibowli', 'Hitech City', 'Banjara Hills', 'Jubilee Hills', 'Kondapur', 'Madhapur', 'Kukatpally', 'Manikonda'],
  },
};

const LocationLanding = () => {
  const { city } = useParams();
  const navigate = useNavigate();
  const location = window.location;
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);

  const type = location.pathname.split('/')[1]; // buy, rent, or interiors
  const meta = LOCATION_META[city?.toLowerCase()] || { name: city, state: '', description: `properties in ${city}`, areas: [] };

  const typeLabels = { buy: 'Buy', rent: 'Rent', interiors: 'Interior Design' };
  const typeLabel = typeLabels[type] || type;

  useEffect(() => {
    fetchListings();
  }, [type, city]);

  const fetchListings = async () => {
    setLoading(true);
    try {
      if (type === 'buy' || type === 'rent') {
        const res = await apiClient.get(`/properties/search?city=${city}&listing_type=${type === 'buy' ? 'sell' : 'rent'}&limit=12`);
        setListings(res.data?.properties || res.data || []);
      }
    } catch (e) {
      console.error('Error fetching location listings:', e);
    } finally {
      setLoading(false);
    }
  };

  const pageTitle = `${typeLabel} Properties in ${meta.name} | ZenStudios`;
  const pageDescription = `Explore ${typeLabel.toLowerCase()} options for ${meta.description}. Find verified listings, expert guidance, and the best deals with ZenStudios.`;

  return (
    <div className="min-h-screen bg-[#F5F5F0]" data-testid="location-landing-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${type} ${meta.name}, real estate ${meta.name}, properties ${meta.name}, ${meta.areas?.join(', ')}`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`https://zenstudios.in/${type}/${city?.toLowerCase()}`} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={`https://zenstudios.in/${type}/${city?.toLowerCase()}`} />
        <meta property="og:type" content="website" />
      </Helmet>

      <div className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 text-sm text-slate-400 mb-4">
            <Link to="/" className="hover:text-white">Home</Link>
            <span>/</span>
            <span className="capitalize">{type}</span>
            <span>/</span>
            <span className="text-white">{meta.name}</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">
            {typeLabel} in {meta.name}
          </h1>
          <p className="text-lg text-slate-300 max-w-2xl">
            {pageDescription}
          </p>
          {meta.areas.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-6">
              {meta.areas.map(area => (
                <span key={area} className="px-3 py-1 bg-white/10 rounded-full text-sm">{area}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {type === 'interiors' ? (
          <div className="text-center py-16">
            <Palette className="w-12 h-12 text-[#15803D] mx-auto mb-4" />
            <h2 className="text-3xl font-bold mb-4">Interior Design Services in {meta.name}</h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Transform your space with ZenStudios. Get AI-powered design quotes and work with expert designers in {meta.name}.
            </p>
            <Button onClick={() => navigate('/interior-design')} className="rounded-none bg-[#15803D]" data-testid="get-quote-cta">
              Get Free Design Quote <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-slate-800 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : listings.length === 0 ? (
          <div className="text-center py-16">
            <Building2 className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">No listings available yet</h2>
            <p className="text-gray-600 mb-6">We're adding new properties in {meta.name} every day.</p>
            <Button onClick={() => navigate(type === 'buy' ? '/buy/properties' : '/rentals/find')} className="rounded-none" data-testid="browse-all-btn">
              Browse All Properties
            </Button>
          </div>
        ) : (
          <div>
            <h2 className="text-2xl font-bold mb-6">{listings.length} Properties Found</h2>
            <div className="grid md:grid-cols-3 gap-6">
              {listings.map((p) => (
                <div
                  key={p.property_id || p.listing_id}
                  className="bg-white border hover:shadow-lg transition-all cursor-pointer"
                  onClick={() => navigate(`/property/${p.property_id || p.listing_id}`)}
                  data-testid={`listing-${p.property_id || p.listing_id}`}
                >
                  {p.images?.[0] && (
                    <img src={p.images[0]} alt={p.title} className="w-full h-48 object-cover" loading="lazy" />
                  )}
                  <div className="p-4">
                    <h3 className="font-semibold truncate">{p.title}</h3>
                    <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                      <MapPin className="w-3.5 h-3.5" />
                      <span>{p.location || p.city || meta.name}</span>
                    </div>
                    <p className="font-mono text-lg text-[#15803D] mt-2">
                      {p.price >= 10000000
                        ? `${(p.price / 10000000).toFixed(2)} Cr`
                        : p.price >= 100000
                        ? `${(p.price / 100000).toFixed(2)} L`
                        : `${p.price?.toLocaleString('en-IN')}`}
                      {type === 'rent' && '/mo'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationLanding;
